/**
 * `kind: NUMBER` — one number, with the unit the canvas declared.
 *
 * ## The unit is a label, and that is the whole point
 *
 * `numberFormat.unit` is `$K` on 8 of the live questions and `%` on 2. It is DISPLAY only:
 * the stored value is the number the BD typed in that unit, so a rule reads `>= 30` against
 * the 30 they entered.
 *
 * That is a deliberate break from the old model, which carried a `scale` as well and stored a
 * ratio while showing a percentage -- so `Q_1_1_10`'s rule `metric['Q_1_1_10'] >= 30` compared
 * 30 against a value that is never above 1, could not fire, and no checker could see why.
 * There is no scale here, so the comparison is always against what the BD saw.
 *
 * NOTE: the canvas editor still has a `scale` field and `toDocument` drops it. Parked by
 * decision; the editor's field should go when that is picked up.
 */
import { Input } from '@amzn/awsui-components-react';

import type { DocNumberFormat } from '../../utils/wire';
import type { AnswerControlProps } from '../answerTypes';
import './NumberAnswer.css';

export interface NumberAnswerProps extends AnswerControlProps {
  format?: DocNumberFormat;
}

export default function NumberAnswer({ format, value, onChange, readOnly }: NumberAnswerProps) {
  const unit = format?.unit;
  return (
    <div className="cpa-num">
      <div className="cpa-num__field">
        <Input
          type="number"
          inputMode="decimal"
          readOnly={readOnly}
          value={value === undefined ? '' : String(value)}
          placeholder="0"
          onChange={({ detail }) => {
            const raw = detail.value;
            if (raw === '') {
              onChange(undefined);
              return;
            }
            const n = Number(raw);
            // Keep the raw string when it is mid-typing ("1.", "-") rather than coercing to
            // NaN: coercing would wipe the field under the BD's cursor.
            onChange(Number.isFinite(n) ? n : raw);
          }}
        />
      </div>
      {/* Beside the input, not inside the prompt. Several live prompts already end in "in
          $K?", so the unit appears twice -- that duplication is in the authored text and is
          for the author to remove, not for this control to hide. */}
      {unit && <span className="cpa-num__unit">{unit}</span>}
    </div>
  );
}
